import React, { useState, useRef } from 'react';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { submissionService } from '../../services/submissionService';
import { ReportSystemInfoTab } from './ReportSystemInfoTab';
import { ReportSystemFieldsTab } from './ReportSystemFieldsTab';

export function ReportSystemEditModal({ system, isOpen, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('info');
  const [discardInfoOpen, setDiscardInfoOpen] = useState(false);
  // Snapshot the info-tab values at mount so we can detect dirty state.
  // (ReportSystems passes key={editingSystem?.id} so this remounts per system.)
  const initialSystemDataRef = useRef({
    name: system?.name || '',
    description: system?.description || '',
    enabled_periods: system?.enabled_periods || [],
    per_country: !!system?.per_country,
  });
  const [systemData, setSystemData] = useState(initialSystemDataRef.current);
  const [periodCounts, setPeriodCounts] = useState({});
  const [loading] = useState(false);

  useAbortableFetch(async ({ isCancelled }) => {
    if (!isOpen || !system?.id) return;
    const counts = await submissionService.getPeriodCountsForSystem('report', system.id);
    if (!isCancelled()) setPeriodCounts(counts);
  }, [isOpen, system?.id]);

  const periodsKey = (arr) => [...(arr || [])].sort().join(',');
  const isInfoDirty =
    ['name', 'description'].some(
      (k) => (systemData[k] || '') !== (initialSystemDataRef.current[k] || '')
    ) || periodsKey(systemData.enabled_periods) !== periodsKey(initialSystemDataRef.current.enabled_periods)
      || !!systemData.per_country !== !!initialSystemDataRef.current.per_country;

  const handleClose = () => {
    if (isInfoDirty && !loading) setDiscardInfoOpen(true);
    else onClose();
  };

  if (!isOpen || !system) return null;

  return (
    <Modal onClose={handleClose}>
      <div className="report-system-edit-modal">
        <h2>Edit Report System: {system.name}</h2>

        <div className="modal-tabs" role="tablist" aria-label="Report system sections">
          {[
            { id: 'info', label: 'System Info' },
            { id: 'fields', label: 'Fields' },
          ].map((t) => (
            <button
              key={t.id}
              id={`report-system-tab-${t.id}`}
              role="tab"
              type="button"
              aria-selected={activeTab === t.id}
              aria-controls={`report-system-panel-${t.id}`}
              className={`modal-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          className="modal-tab-content"
          role="tabpanel"
          id={`report-system-panel-${activeTab}`}
          aria-labelledby={`report-system-tab-${activeTab}`}
        >
          {activeTab === 'info' && (
            <ReportSystemInfoTab
              systemData={systemData}
              setSystemData={setSystemData}
              loading={loading}
              onSave={onSave}
              onGoToFields={() => setActiveTab('fields')}
              onCancel={handleClose}
              periodCounts={periodCounts}
            />
          )}
          {activeTab === 'fields' && (
            <ReportSystemFieldsTab systemId={system.id} />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={discardInfoOpen}
        title="Discard unsaved info-tab changes?"
        message="Field changes are saved immediately, but System Info edits aren't. Closing now will lose them."
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setDiscardInfoOpen(false); onClose(); }}
        onCancel={() => setDiscardInfoOpen(false)}
      />
    </Modal>
  );
}
